import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Heart, MessageCircle, Calendar, BookOpen, Download, Printer } from "lucide-react";
import AiInsights from "@/components/schools/AiInsights";
import DemoFlow from "@/components/schools/DemoFlow";
import SchoolKpi from "@/components/schools/SchoolKpi";
import { student, parentKpis, parentConversationStarters, aiInsightsParent, studentDimensions } from "@/data/greenwoodPersonas";
import { downloadCsv } from "@/lib/csv";

// Cohort benchmarks — parent-friendly comparison to peer families.
const parentBenchmarks = { weeklyReflections: 3, moduleProgress: 62, mentorSessions: 2, clarityIndex: 71 } as const;

export default function ParentDashboard() {
  const exportParent = () => {
    const rows: (string | number)[][] = [
      ["WeKIT Education Intelligence™ — Parent snapshot"],
      ["Student", student.name],
      ["Grade", `${student.grade}-${student.section}`],
      ["Mentor", student.mentor],
      ["Generated", new Date().toISOString()],
      [],
      ["Metric", "Value", "Peer families"],
      ["Reflections this week", parentKpis.weeklyReflections, parentBenchmarks.weeklyReflections],
      ["Module progress (%)", parentKpis.moduleProgress, parentBenchmarks.moduleProgress],
      ["Mentor sessions", parentKpis.mentorSessions, parentBenchmarks.mentorSessions],
      ["Career Clarity Index™", parentKpis.clarityIndex, parentBenchmarks.clarityIndex],
      [],
      ["Dimension", "Score"],
      ...studentDimensions.map((d) => [d.dim, d.score]),
    ];
    downloadCsv(`${student.name.toLowerCase().replace(/\s+/g, "-")}-parent-snapshot-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  return (
    <>
      <PageHead title={`Parent Dashboard | ${student.name}`} description="Parent dashboard for WeKIT Education Intelligence™" path="/schools/parent" />
      <div className="container max-w-7xl py-8 space-y-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <Heart className="w-3.5 h-3.5 text-primary" /> Parent View
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Following {student.name}'s journey</h1>
            <p className="text-muted-foreground mt-1">
              Grade {student.grade}-{student.section} · Mentor {student.mentor}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="rounded-full">🔥 {student.reflectionStreak}-day reflection streak</Badge>
            <Button size="sm" variant="outline" onClick={exportParent}><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/schools/parent/onepager"><Printer className="w-3.5 h-3.5 mr-1" /> One-pager</Link>
            </Button>
            <Button size="sm" variant="outline" disabled title="Mentor sessions open when your school enables mentoring"><Calendar className="w-3.5 h-3.5 mr-1" /> Book mentor session</Button>
          </div>
        </header>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SchoolKpi icon={<MessageCircle className="w-4 h-4" />} label="Reflections this week" value={parentKpis.weeklyReflections}       hint="Target: 5"           tone="positive" series={[2, 3, 3, 4, 4, 5, 5]} benchmark={parentBenchmarks.weeklyReflections} />
          <SchoolKpi icon={<BookOpen className="w-4 h-4" />}      label="Module progress"       value={`${parentKpis.moduleProgress}%`}  hint="All studios combined" tone="positive" series={[52, 58, 63, 68, 72, 75, 78]} benchmark={parentBenchmarks.moduleProgress} benchmarkSuffix="%" />
          <SchoolKpi icon={<Calendar className="w-4 h-4" />}      label="Mentor sessions"       value={parentKpis.mentorSessions}        hint="This term"                            series={[1, 1, 2, 2, 3, 3, 4]} benchmark={parentBenchmarks.mentorSessions} />
          <SchoolKpi icon={<Sparkles className="w-4 h-4" />}      label="Career Clarity Index™" value={parentKpis.clarityIndex}          hint="+8 this term"         tone="positive" series={[70, 72, 74, 76, 78, 80, 82]} benchmark={parentBenchmarks.clarityIndex} />
        </section>

        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">How {student.name.split(" ")[0]} is growing</CardTitle>
              <p className="text-xs text-muted-foreground">Human Development dimensions — parent-friendly view</p>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              {studentDimensions.map((d) => (
                <div key={d.dim} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{d.dim}</span>
                    <span className="tabular-nums text-muted-foreground">{d.score}</span>
                  </div>
                  <Progress value={d.score} className="h-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Conversation starters</CardTitle>
              <p className="text-xs text-muted-foreground">Try one at dinner this week</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {parentConversationStarters.map((c, i) => (
                <div key={i} className="rounded-xl border p-3 text-sm leading-relaxed">
                  {c}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <AiInsights
          insights={aiInsightsParent}
          title="What we're seeing this month"
          subtitle="A gentle read on your child's journey"
          sendToParent={{
            subject: `Sharing this with you — ${student.name}`,
            intro: `Hi,\n\nSharing a note from ${student.name}'s WeKIT Career Clarity 360™ journey this week:`,
            signature: `Forwarded from the family dashboard · WeKIT Education Intelligence™`,
          }}
        />
        <DemoFlow />
      </div>
    </>
  );
}
